import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { SprayCan, Camera, Images, X, FileText } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextInput, TextArea, Select, Field, Badge } from "@/components/ui-kit";
import { StickySubmit } from "./TemperatureForm";
import { createRecord, updateRecord, uploadFile } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { getUser } from "@/lib/auth";

const FREQUENCIES = ["Diari", "Setmanal", "Quinzenal", "Mensual", "Quan calgui"];

export default function CleaningForm() {
  const navigate = useNavigate();
  const editRecord = useLocation().state?.editRecord;
  const today  = new Date().toISOString().slice(0, 10);
  const signer = getUser()?.username || "";
  const [form, setForm] = useState(editRecord
    ? { date: editRecord.date, zone: editRecord.zone, product_used: editRecord.product_used, frequency: editRecord.frequency, method: editRecord.method || "", validation: editRecord.validation, incident: editRecord.incident || "" }
    : { date: today, zone: "Obrador", product_used: "", frequency: "Diari", method: "", validation: "OK", incident: "" });
  const [photos, setPhotos] = useState(editRecord?.photos || []);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // permet re-seleccionar el mateix fitxer
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const resized = await resizeImage(file);
        const { url } = await uploadFile(resized);
        setPhotos((prev) => [...prev, url]);
      }
    } catch {
      toast.error("No s'ha pogut pujar la foto");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (url) => setPhotos((prev) => prev.filter((u) => u !== url));

  const submit = async () => {
    if (!form.zone || !form.product_used)
      return toast.error("Omple tots els camps obligatoris");
    setSubmitting(true);
    try {
      const payload = { ...form, photos, signer };
      if (editRecord) await updateRecord("cleaning", editRecord.id, payload);
      else await createRecord("cleaning", payload);
      toast.success(editRecord ? "Neteja modificada" : "Neteja registrada");
      navigate("/history?type=cleaning");
    } catch { toast.error("Error desant el registre"); }
    finally   { setSubmitting(false); }
  };

  return (
    <>
      <Header title="Neteja i desinfecció" />
      <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-white"><SprayCan size={22} /></div>
          <div><div className="label-eyebrow">Pla 2</div><h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Neteja i desinfecció</h2></div>
        </div>
        <Link to="/cleaning-products" className="mt-4 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm font-semibold text-terracotta shadow-card transition-colors hover:border-terracotta">
          <FileText size={16} /> Productes i fitxes tècniques de neteja
        </Link>
        <div className="mt-4 space-y-4">
          <Card>
            <div className="space-y-4">
              <Field label="Data"><input data-testid="cleaning-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="h-14 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" /></Field>
              <Field label="Zona"><Select testid="cleaning-zone" value={form.zone} onChange={(e) => set("zone", e.target.value)}>
                <option value="Obrador">Obrador</option>
                <option value="Botiga">Botiga</option>
                <option value="Magatzem">Magatzem</option>
              </Select></Field>
              <Field label="Producte utilitzat"><TextInput testid="cleaning-product" placeholder="Ex: Desinfectant clorat" value={form.product_used} onChange={(e) => set("product_used", e.target.value)} /></Field>
              <Field label="Freqüència"><Select testid="cleaning-frequency" value={form.frequency} onChange={(e) => set("frequency", e.target.value)}>{FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}</Select></Field>
            </div>
          </Card>
          <Card>
            <Field label="Fotos (opcional)">
              <div className="flex flex-wrap gap-3">
                {photos.map((url) => (
                  <div key={url} className="relative h-20 w-20">
                    <img src={url} alt="Foto neteja" className="h-20 w-20 rounded-xl border border-line object-cover" />
                    <button
                      type="button"
                      data-testid="cleaning-photo-remove"
                      onClick={() => removePhoto(url)}
                      className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-ember text-white shadow-card"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-line bg-white text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                  <Camera size={20} />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">{uploading ? "Pujant…" : "Càmera"}</span>
                  <input data-testid="cleaning-photo-camera" type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFiles} disabled={uploading} />
                </label>
                <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-line bg-white text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                  <Images size={20} />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">Galeria</span>
                  <input data-testid="cleaning-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={uploading} />
                </label>
              </div>
            </Field>
          </Card>
          <Card>
            <div className="space-y-4">
              <Field label="Mètode / observacions (opcional)"><TextArea testid="cleaning-method" placeholder="Com s'ha fet la neteja" value={form.method} onChange={(e) => set("method", e.target.value)} /></Field>
              <Field label="Validació">
                <div className="flex items-center gap-3">
                  <Select testid="cleaning-validation" value={form.validation} onChange={(e) => set("validation", e.target.value)} className="flex-1">
                    <option value="OK">Correcte</option>
                    <option value="DEFICIENCIA">Deficiència</option>
                  </Select>
                  <Badge status={form.validation} />
                </div>
              </Field>
              <Field label="Incidència detectada (opcional)"><TextArea testid="cleaning-incident" placeholder="Si hi ha algun problema, descriu-lo aquí" value={form.incident} onChange={(e) => set("incident", e.target.value)} /></Field>
            </div>
          </Card>
        </div>
      </div>
      <StickySubmit onClick={submit} disabled={submitting || uploading} label={submitting ? "Desant..." : editRecord ? "Desar canvis" : "Desar neteja"} />
    </>
  );
}
